import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import { togglePermissions } from '../../../../stores/hubspotPanelStore';
import { CopyableValue, Field, FieldGrid } from '../../../common/detailParts';
import { PanelSection } from '../../PanelSection';
import { PermissionGroup } from '../../PermissionBadge';
import type { AndroidDetails } from '../../types';
import { getAndroidVersionName } from '../../utils';

interface AndroidDetailsSectionProps {
  androidDetails?: AndroidDetails;
  categorizedPermissions: {
    normal: string[];
    dangerous: string[];
    special: string[];
  };
  isPermissionsExpanded: boolean;
}

export function AndroidDetailsSection({
  androidDetails,
  categorizedPermissions,
  isPermissionsExpanded,
}: AndroidDetailsSectionProps) {
  if (!androidDetails) {
    return null;
  }

  const permissionCount = androidDetails.permissions?.length ?? 0;

  return (
    <PanelSection title="Android">
      <FieldGrid cols={2}>
        <Field label="Package" className="sm:col-span-2">
          <CopyableValue
            value={androidDetails.android_package}
            className="-my-1.5"
          />
        </Field>
        <Field label="Version">
          {androidDetails.version} ({androidDetails.version_code})
        </Field>
        <Field label="Min SDK">
          {androidDetails.min_sdk} (
          {getAndroidVersionName(androidDetails.min_sdk)})
        </Field>
        {androidDetails.locales && androidDetails.locales.length > 0 && (
          <Field label="Locales" className="sm:col-span-2">
            {androidDetails.locales.join(', ')}
          </Field>
        )}
        {androidDetails.cert_fingerprint && (
          <Field label="Cert SHA-256" className="sm:col-span-2">
            <CopyableValue
              value={androidDetails.cert_fingerprint}
              truncate={false}
              className="-my-1.5 text-xs"
            />
          </Field>
        )}
      </FieldGrid>

      {permissionCount > 0 && (
        <div className="mt-5 border-t border-white/5 pt-3 text-sm">
          <button
            type="button"
            onClick={togglePermissions}
            aria-expanded={isPermissionsExpanded}
            className="group flex w-full items-center justify-between gap-2 py-1 text-left font-semibold text-white transition-colors hover:text-teal-300"
          >
            <span className="flex flex-wrap items-center gap-2">
              Permissions
              <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-xs font-normal text-neutral-300">
                {permissionCount}
              </span>
              {categorizedPermissions.special.length > 0 && (
                <span className="rounded-full border border-red-500/20 bg-red-500/10 px-2 py-0.5 text-xs font-normal text-red-300">
                  {categorizedPermissions.special.length} special
                </span>
              )}
              {categorizedPermissions.dangerous.length > 0 && (
                <span className="rounded-full border border-yellow-500/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-normal text-yellow-300">
                  {categorizedPermissions.dangerous.length} dangerous
                </span>
              )}
            </span>
            <ExpandMoreIcon
              className={`size-5 shrink-0 fill-current text-neutral-500 transition-transform ${
                isPermissionsExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>

          {isPermissionsExpanded && (
            <div className="mt-3 max-h-96 space-y-3 overflow-y-auto pr-1">
              <PermissionGroup
                title="Special"
                count={categorizedPermissions.special.length}
                color="text-red-400"
                permissions={categorizedPermissions.special}
                category="special"
              />
              <PermissionGroup
                title="Dangerous"
                count={categorizedPermissions.dangerous.length}
                color="text-yellow-400"
                permissions={categorizedPermissions.dangerous}
                category="dangerous"
              />
              <PermissionGroup
                title="Normal"
                count={categorizedPermissions.normal.length}
                color="text-green-400"
                permissions={categorizedPermissions.normal}
                category="normal"
              />
            </div>
          )}
        </div>
      )}
    </PanelSection>
  );
}
